import { ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { EffectFeedbackInterface } from '@campus/dal';
import { ENVIRONMENT_UI_TOKEN, EnvironmentUIInterface } from '@campus/environment';
import { Action } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, filter, map, take, tap } from 'rxjs';
import { ErrorDialogViewModel } from './error-dialog.viewmodel';

import '@diekeure/dcr-components/dcr-dialog.js';
import '@diekeure/dcr-components/dcr-inline-button.js';

interface ErrorDialogDataInterface {
  id: string;
  icon: string;
  title: string;
  message: string;
  type: 'confirm' | 'confirm-cancel';
  confirmLabel: string;
  confirmAction: Action | null;
  cancelLabel?: string;
  cancelAction?: Action | null;
  supportMailAddress: string;
}

@Component({
  standalone: false,
  selector: 'campus-error-dialog',
  templateUrl: './error-dialog.component.html',
})
export class ErrorDialogComponent implements OnInit {
  @Input() supportLabel = 'de helpdesk';

  public errorFeedback$: Observable<ErrorDialogDataInterface | undefined>;
  public isOpen$: Observable<boolean>;
  private shouldOpen$ = new BehaviorSubject(false);
  private isClosing$: BehaviorSubject<{ feedbackId: string; action: Action } | undefined> = new BehaviorSubject(
    undefined
  );

  private uiEnvironment: EnvironmentUIInterface = inject(ENVIRONMENT_UI_TOKEN);
  private errorDialogViewModel: ErrorDialogViewModel = inject<ErrorDialogViewModel>(ErrorDialogViewModel);

  public ngOnInit(): void {
    this.errorFeedback$ = this.getErrorFeedback$();
    this.isOpen$ = combineLatest([this.shouldOpen$, this.isClosing$]).pipe(
      map(([shouldOpen, isClosing]) => {
        return shouldOpen && !isClosing;
      })
    );
  }

  public onFeedbackDialogConfirm(feedback: ErrorDialogDataInterface): void {
    const confirmAction = feedback.confirmAction;
    const event = { feedbackId: feedback.id, action: confirmAction };
    this.closeDialog(event);
  }

  public onFeedbackDialogCancel(feedback: ErrorDialogDataInterface): void {
    const cancelAction = feedback.cancelAction;
    const event = { feedbackId: feedback.id, action: cancelAction };
    this.closeDialog(event);
  }

  public closedDialog() {
    this.isClosing$.pipe(take(1)).subscribe((event) => {
      if (event) {
        this.errorDialogViewModel.onFeedbackDismissed(event);
      }
      this.isClosing$.next(undefined);
    });
  }

  public closeDialog(event: { feedbackId: string; action: Action }) {
    this.isClosing$.next(event);
  }

  private getErrorFeedback$(): Observable<ErrorDialogDataInterface | undefined> {
    return this.errorDialogViewModel.errorFeedback$.pipe(
      map((feedback: EffectFeedbackInterface): ErrorDialogDataInterface | undefined => {
        if (!feedback) return undefined;

        const feedbackActions = feedback.userActions || [];
        const [confirmAction, cancelAction] = feedbackActions;

        return {
          id: feedback.id,
          icon: 'shared:error',
          title: feedback.title || 'Oeps! Er ging iets fout.',
          message: feedback.message || 'Probeer het opnieuw.',
          type: feedbackActions.length > 1 ? 'confirm-cancel' : 'confirm',
          confirmLabel: confirmAction?.title || 'Oké',
          confirmAction: confirmAction?.userAction || null,
          cancelLabel: cancelAction?.title || 'Annuleren',
          cancelAction: cancelAction?.userAction || null,
          supportMailAddress: this.uiEnvironment?.contact?.general?.mailAddress,
          supportLabel: this.supportLabel,
        } as ErrorDialogDataInterface;
      }),
      tap(() => {
        this.shouldOpen$.next(true);
      })
    );
  }
}
